/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useBrandMetrics } from '../hooks/useBrandMetrics';
import DigitalMetricsDashboard from '../components/DigitalMetricsDashboard';
import BrandComparison from '../components/BrandComparison';
import PostsDashboard from '../components/PostsDashboard';
import BrandSwitcher from '../components/BrandSwitcher';
import DeepDiveDashboard from '../components/DeepDiveDashboard';
import DemographicFilter, { DemographicFilters } from '../components/DemographicFilter';
import ExportMenu from '../components/ExportMenu';
import { Line, Radar } from 'react-chartjs-2';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { useDateFilter } from '../contexts/DateFilterContext';
import { isDemoMode } from '../services';
import { getBrandTrendData, availableMonths } from '../services/monthlyMockData';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Main Dashboard Page with Traditional, Digital, and Combined views
 */
const DashboardPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { selectedDate, getMonthFromDate } = useDateFilter();
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'executive' | 'deepdive' | 'digital' | 'comparison' | 'posts'>('executive');
  const [healthIndex, setHealthIndex] = useState<any>(null);
  const [insights, setInsights] = useState<string[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [demographicFilters, setDemographicFilters] = useState<DemographicFilters>({
    age: [],
    gender: [],
    nationality: [],
    city: [],
    sec: [],
    decisionMaker: [],
    householdCars: [],
    residenceType: []
  });
  const { metrics, allBrands, digitalMetrics, loading, error } = useBrandMetrics(selectedBrand);

  // Set default brand on load and fetch health index
  useEffect(() => {
    if (allBrands.length > 0 && !selectedBrand) {
      setSelectedBrand(allBrands[0].brand_id);
    }
  }, [allBrands]);

  // Fetch Brand Health Index when brand changes
  useEffect(() => {
    if (selectedBrand) {
      fetchHealthIndex(selectedBrand);
    }
  }, [selectedBrand]);

  // Fetch insights only for executive tab to prevent unnecessary calls
  useEffect(() => {
    if (selectedBrand && activeTab === 'executive') {
      fetchInsights(selectedBrand, activeTab);
    }
  }, [selectedBrand, activeTab]);

  const fetchHealthIndex = async (brandId: string) => {
    try {
      if (isDemoMode) {
        // Mock health index data - matching expected format
        const mockHealthIndex = {
          health_index: 78.5,
          percentile: 75,
          category: 'Beverages',
          rank: 2,
          total_brands: 5,
          trend: 'up',
          change: 2.3,
          breakdown: {
            awareness_score: 82,
            trust_score: 75,
            quality_score: 80,
            nps_score: 52,
            loyalty_score: 68
          },
          industry_averages: {
            awareness: 55,
            consideration: 38,
            usage: 32,
            trust: 48,
            quality: 52,
            value: 47,
            innovation: 42,
            loyalty: 28
          }
        };
        setHealthIndex(mockHealthIndex);
      } else {
        const response = await axios.get(`/api/v2/metrics/brands/${brandId}/health-index`);
        setHealthIndex(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch health index:', error);
    }
  };

  // Track if a fetch is in progress to prevent duplicate calls
  const fetchInsightsRef = useRef<boolean>(false);
  
  const fetchInsights = async (brandId: string, tab: string) => {
    // Prevent duplicate calls
    if (fetchInsightsRef.current) {
      return;
    }

    fetchInsightsRef.current = true;
    setInsightsLoading(true);
    setInsights([]); // Clear previous insights to show loading state

    // Add minimum loading time for better UX
    const startTime = Date.now();

    try {
      let insightsData: string[] | null = null;

      if (isDemoMode) {
        // Mock insights data
        insightsData = [
          `Your brand shows strong performance with a total equity score of ${metrics?.combined_score.toFixed(1)}%.`,
          `Traditional metrics indicate high trust (${metrics?.traditional.trust.toFixed(0)}%) and quality perception (${metrics?.traditional.quality.toFixed(0)}%).`,
          `Digital sentiment is ${metrics?.digital.sentiment_score.toFixed(0)}% positive with ${metrics?.digital.mentions_count.toLocaleString()} total mentions.`,
          `NPS score of ${metrics?.traditional.nps} indicates strong customer loyalty and advocacy.`,
          `Awareness to consideration conversion rate suggests effective brand communication.`
        ];
      } else {
        // Call the LLM-powered insights endpoint
        const response = await axios.get(`/api/v2/metrics/brands/${brandId}/insights`);
        if (response.data && response.data.insights) {
          insightsData = response.data.insights;
        }
      }

      // Ensure minimum loading time of 500ms for visibility
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 500) {
        await new Promise(resolve => setTimeout(resolve, 500 - elapsedTime));
      }

      if (insightsData) {
        setInsights(insightsData);
      } else {
        // Fallback to rule-based insights if API doesn't return expected format
        if (metrics) {
          const newInsights = generateInsights(metrics, healthIndex, tab);
          setInsights(newInsights);
        }
      }
    } catch (error) {
      console.error('Failed to fetch AI insights, using fallback:', error);
      // Fallback to rule-based insights
      if (metrics) {
        const newInsights = generateInsights(metrics, healthIndex, tab);
        setInsights(newInsights);
      }
    } finally {
      setInsightsLoading(false);
      fetchInsightsRef.current = false;
    }
  };

  const generateInsights = (metricsData: any, indexData: any, tab: string): string[] => {
    const insights: string[] = [];
    
    if (tab === 'combined' && metricsData) {
      // Awareness to consideration conversion
      const conversionRate = (metricsData.traditional.consideration / metricsData.traditional.awareness) * 100;
      insights.push(`Your awareness to consideration conversion rate is ${conversionRate.toFixed(1)}%, ${conversionRate > 70 ? 'exceeding' : 'below'} industry benchmarks.`);
      
      // Quality perception
      if (metricsData.traditional.quality > 50) {
        insights.push(`Quality perception at ${metricsData.traditional.quality.toFixed(1)}% demonstrates strong product confidence among consumers.`);
      }
      
      // NPS analysis
      const npsCategory = metricsData.traditional.nps > 50 ? 'Excellent' : metricsData.traditional.nps > 0 ? 'Good' : 'Needs Improvement';
      insights.push(`NPS score of ${metricsData.traditional.nps} (${npsCategory}) ${metricsData.traditional.nps > 0 ? 'indicates positive word-of-mouth potential' : 'suggests focus on customer satisfaction is needed'}.`);
      
      // Digital presence
      insights.push(`Digital engagement rate of ${metricsData.digital.engagement_rate.toFixed(1)}% with ${metricsData.digital.mentions_count.toLocaleString()} monthly mentions shows ${metricsData.digital.engagement_rate > 5 ? 'strong' : 'moderate'} social media traction.`);
      
      // Brand health index
      if (indexData) {
        insights.push(`Brand Health Index of ${indexData.health_index.toFixed(0)} places you in the ${indexData.percentile.toFixed(0)}th percentile, ${indexData.percentile > 75 ? 'leading' : indexData.percentile > 50 ? 'above average in' : 'trailing'} the category.`);
      }
      
      // Value perception
      const valueGap = metricsData.traditional.quality - metricsData.traditional.value;
      if (valueGap > 10) {
        insights.push(`Quality (${metricsData.traditional.quality.toFixed(1)}%) exceeds value perception (${metricsData.traditional.value.toFixed(1)}%) by ${valueGap.toFixed(1)}pp, suggesting pricing optimization opportunity.`);
      }
      
      // Loyalty drivers
      insights.push(`Primary brand selection at ${metricsData.traditional.primary_brand.toFixed(1)}% with ${metricsData.traditional.loyalty.toFixed(1)}% loyalty indicates ${metricsData.traditional.primary_brand > 30 ? 'strong market position' : 'growth opportunity in repeat purchase behavior'}.`);
    }
    
    return insights;
  };

  const handleBrandToggle = (brandId: string) => {
    setSelectedBrand(brandId);
  };

  const handleAskBrandDoctor = () => {
    if (metrics) {
      // Build comprehensive context for the Brand Doctor
      const contextParts = [];
      
      // Add key metrics
      contextParts.push(`Brand: ${metrics.brand_name}`);
      contextParts.push(`Awareness: ${metrics.traditional.awareness.toFixed(1)}%`);
      contextParts.push(`Consideration: ${metrics.traditional.consideration.toFixed(1)}%`);
      contextParts.push(`Usage: ${metrics.traditional.usage.toFixed(1)}%`);
      contextParts.push(`NPS Score: ${metrics.traditional.nps}`);
      contextParts.push(`Quality Perception: ${metrics.traditional.quality.toFixed(1)}%`);
      contextParts.push(`Trust: ${metrics.traditional.trust.toFixed(1)}%`);
      contextParts.push(`Value: ${metrics.traditional.value.toFixed(1)}%`);
      
      // Add insights about performance
      if (metrics.traditional.nps > 50) {
        contextParts.push('Strong NPS indicates high advocacy potential');
      } else if (metrics.traditional.nps < 0) {
        contextParts.push('Negative NPS suggests urgent attention needed');
      }
      
      const prompt = `Analyze the brand health for ${metrics.brand_name}:\n\n${contextParts.join('\n')}\n\nProvide strategic insights and actionable recommendations to improve brand performance.`;
      
      navigate('/', { 
        state: { 
          initialMessage: prompt,
          brandId: selectedBrand,
          brandMetrics: metrics 
        }
      });
    }
  };

  // Show loading state while data is being fetched
  if (!metrics || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-text-muted mt-4">Loading brand health data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <i className="fas fa-exclamation-triangle text-4xl text-red-400 mb-4"></i>
          <p className="text-text-muted">Error loading data: {error}</p>
        </div>
      </div>
    );
  }

  // Combined view data - Comprehensive brand health radar
  const combinedData = metrics ? {
    labels: ['Awareness', 'Consideration', 'Usage', 'Trust', 'Quality', 'Value', 'Innovation', 'Loyalty'],
    datasets: [{
      label: metrics.brand_name,
      data: [
        metrics.traditional.awareness,
        metrics.traditional.consideration,
        metrics.traditional.usage,
        metrics.traditional.trust,
        metrics.traditional.quality,
        metrics.traditional.value,
        metrics.traditional.innovation,
        metrics.traditional.loyalty
      ],
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      borderColor: 'rgba(59, 130, 246, 1)',
      pointBackgroundColor: 'rgba(59, 130, 246, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(59, 130, 246, 1)'
    }, {
      label: 'Industry Average',
      data: healthIndex ? [
        healthIndex.industry_averages.awareness || 50,
        healthIndex.industry_averages.consideration || 35,
        healthIndex.industry_averages.usage || 30,
        healthIndex.industry_averages.trust || 45,
        healthIndex.industry_averages.quality || 50,
        healthIndex.industry_averages.value || 45,
        healthIndex.industry_averages.innovation || 40,
        healthIndex.industry_averages.loyalty || 25
      ] : [50, 35, 30, 45, 50, 45, 40, 25],
      backgroundColor: 'rgba(156, 163, 175, 0.1)',
      borderColor: 'rgba(156, 163, 175, 0.5)',
      borderDash: [5, 5],
      pointBackgroundColor: 'rgba(156, 163, 175, 0.5)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(156, 163, 175, 0.5)'
    }]
  } : null;

  // Additional metrics for comprehensive view
  const additionalMetricsData = metrics ? {
    labels: ['Taste', 'Texture', 'Natural', 'Pride', 'Advocacy'],
    datasets: [{
      label: 'Product Attributes',
      data: [
        (metrics.traditional as any).taste || 50,
        (metrics.traditional as any).texture || 50,
        (metrics.traditional as any).natural || 45,
        metrics.traditional.pride,
        metrics.traditional.advocacy
      ],
      backgroundColor: 'rgba(16, 185, 129, 0.2)',
      borderColor: 'rgba(16, 185, 129, 1)',
      pointBackgroundColor: 'rgba(16, 185, 129, 1)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(16, 185, 129, 1)'
    }]
  } : null;

  // Theme-aware chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: isDark ? '#E5E7EB' : '#374151'
        }
      }
    },
    scales: {
      r: {
        grid: {
          color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          circular: true
        },
        pointLabels: {
          color: isDark ? '#E5E7EB' : '#1F2937',
          font: {
            size: 14,
            weight: '500' as const
          },
          padding: 15
        },
        ticks: {
          color: isDark ? '#9CA3AF' : '#4B5563',
          backdropColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.9)',
          backdropPadding: 3,
          stepSize: 20,
          max: 100,
          min: 0,
          font: {
            size: 12
          },
          callback: function(value: any) {
            return value + '%';
          }
        },
        suggestedMin: 0,
        suggestedMax: 100
      }
    }
  };

  return (
    <div className="min-h-screen p-6 bg-background">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Brand Health Dashboard</h1>
            <p className="text-text-muted">Real-time brand performance metrics from surveys and social media</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Elegant Brand Switcher */}
            <BrandSwitcher
              brands={allBrands}
              selectedBrand={allBrands.find(b => b.brand_id === selectedBrand) || null}
              onBrandSelect={setSelectedBrand}
              recentBrands={[selectedBrand].filter(Boolean)}
            />

            {/* Demographic Filters */}
            <DemographicFilter onFiltersChange={setDemographicFilters} />

            {/* Export Menu */}
            {metrics && (
              <ExportMenu 
                data={metrics}
                dashboardElementId="dashboard-content"
                brandName={metrics.brand_name}
                exportType="all"
                filename={`${metrics.brand_name.toLowerCase().replace(/\s+/g, '_')}_brand_health`}
              />
            )}

            {/* Ask Brand Doctor Button */}
            <button
              onClick={handleAskBrandDoctor}
              className="flex items-center gap-1.5 px-2.5 py-1 border border-border/50 rounded-full hover:border-primary/40 hover:bg-primary/5 transition-all"
            >
              <i className="fas fa-stethoscope text-text-muted text-xs"></i>
              <span className="text-foreground text-sm">Ask Brand Doctor</span>
            </button>
          </div>
        </div>
      </div>


      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-8 border-b border-border overflow-x-auto">
        <button
          onClick={() => setActiveTab('executive')}
          className={`px-4 py-3 font-medium transition-all whitespace-nowrap relative ${
            activeTab === 'executive'
              ? 'text-primary'
              : 'text-text-muted hover:text-foreground'
          }`}
        >
          <i className="fas fa-chart-line mr-2"></i>
          Executive Summary
          {activeTab === 'executive' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('deepdive')}
          className={`px-4 py-3 font-medium transition-all whitespace-nowrap relative ${
            activeTab === 'deepdive'
              ? 'text-green-600 dark:text-green-400'
              : 'text-text-muted hover:text-foreground'
          }`}
        >
          <i className="fas fa-microscope mr-2"></i>
          Traditional Health
          {activeTab === 'deepdive' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-green-600 dark:bg-green-400 rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('digital')}
          className={`px-4 py-3 font-medium transition-all whitespace-nowrap relative ${
            activeTab === 'digital'
              ? 'text-cyan-600 dark:text-cyan-400'
              : 'text-text-muted hover:text-foreground'
          }`}
        >
          <i className="fas fa-globe mr-2"></i>
          Digital Health
          {activeTab === 'digital' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cyan-600 dark:bg-cyan-400 rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('comparison')}
          className={`px-4 py-3 font-medium transition-all whitespace-nowrap relative ${
            activeTab === 'comparison'
              ? 'text-purple-600 dark:text-purple-400'
              : 'text-text-muted hover:text-foreground'
          }`}
        >
          <i className="fas fa-chart-bar mr-2"></i>
          Comparison
          {activeTab === 'comparison' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-600 dark:bg-purple-400 rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('posts')}
          className={`px-4 py-3 font-medium transition-all whitespace-nowrap relative ${
            activeTab === 'posts'
              ? 'text-orange-600 dark:text-orange-400'
              : 'text-text-muted hover:text-foreground'
          }`}
        >
          <i className="fas fa-newspaper mr-2"></i>
          Posts
          {activeTab === 'posts' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-600 dark:bg-orange-400 rounded-full" />
          )}
        </button>
      </div>

      {/* Content based on active tab */}
      <div id="dashboard-content">
        {metrics && (
          <>
            {activeTab === 'executive' && (
              <div className="space-y-6">
              {/* Combined Overview */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Brand Equity & Health Index - First Card */}
                <div className="bg-card rounded-2xl p-6 border border-[var(--border)] relative overflow-hidden">
                  {/* New Banner */}
                  <div
                    className="absolute top-0 right-0 text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl shadow-lg"
                    style={{
                      animation: 'flashColors 1s ease-in-out infinite',
                      background: 'linear-gradient(90deg, #10b981, #facc15)'
                    }}
                  >
                    ✨ NEW
                  </div>
                  <style>{`
                    @keyframes flashColors {
                      0%, 100% { background: linear-gradient(90deg, #10b981, #22c55e); }
                      50% { background: linear-gradient(90deg, #facc15, #eab308); }
                    }
                  `}</style>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-foreground">Brand Equity Overview</h3>
                    <i className="fas fa-heartbeat text-secondary text-2xl"></i>
                  </div>
                  {healthIndex ? (
                    <div>
                      {/* Hero Metrics Row */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        {/* Brand Health Index */}
                        <div className="text-center p-3 bg-background-tertiary/50 rounded-xl">
                          <p className="text-xs text-text-muted mb-1">Brand Health Index</p>
                          <div className="text-3xl font-bold bg-gradient-primary text-transparent bg-clip-text">
                            {healthIndex.health_index.toFixed(0)}
                          </div>
                          <p className="text-xs text-text-muted">vs Avg (100)</p>
                        </div>
                        {/* Total Brand Equity */}
                        <div className="text-center p-3 bg-background-tertiary/50 rounded-xl">
                          <p className="text-xs text-text-muted mb-1">Total Brand Equity</p>
                          <div className="text-3xl font-bold text-purple-500">
                            {((metrics.traditional.awareness + metrics.traditional.trust + metrics.traditional.quality + metrics.traditional.loyalty) / 4 * 0.6 +
                              (metrics.digital?.sentiment_score || 70) * 0.4).toFixed(1)}
                          </div>
                          <p className="text-xs text-text-muted">Combined Score</p>
                        </div>
                      </div>

                      {/* Equity Breakdown */}
                      <div className="space-y-3 mb-4">
                        {/* Traditional Brand Equity */}
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-text-muted">Traditional Equity</span>
                            <span className="font-semibold text-green-400">
                              {((metrics.traditional.awareness + metrics.traditional.trust + metrics.traditional.quality + metrics.traditional.loyalty) / 4).toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-background-tertiary rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${(metrics.traditional.awareness + metrics.traditional.trust + metrics.traditional.quality + metrics.traditional.loyalty) / 4}%` }}
                            ></div>
                          </div>
                        </div>
                        {/* Digital Brand Equity */}
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-text-muted">Digital Equity</span>
                            <span className="font-semibold text-cyan-400">
                              {(metrics.digital?.sentiment_score || 70).toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-background-tertiary rounded-full h-2">
                            <div
                              className="bg-cyan-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${metrics.digital?.sentiment_score || 70}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>

                      {/* Key Performance Indicators */}
                      <div className="pt-3 border-t border-[var(--border)]">
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div>
                            <p className="text-lg font-bold text-foreground">{healthIndex.percentile.toFixed(0)}th</p>
                            <p className="text-xs text-text-muted">Percentile</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-foreground">#{healthIndex.rank || 2}</p>
                            <p className="text-xs text-text-muted">Category Rank</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-foreground">{metrics.digital?.share_of_voice?.toFixed(0) || 25}%</p>
                            <p className="text-xs text-text-muted">Share of Voice</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-32">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  )}
                </div>

                {/* Traditional Summary */}
                <div className="bg-card rounded-2xl p-6 border border-[var(--border)]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-foreground">Traditional Health</h3>
                    <i className="fas fa-clipboard-list text-green-400 text-2xl"></i>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-text-muted">Awareness</span>
                      <span className="text-foreground font-semibold">{metrics.traditional.awareness.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">NPS Score</span>
                      <span className="text-foreground font-semibold">{metrics.traditional.nps}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Loyalty</span>
                      <span className="text-foreground font-semibold">{metrics.traditional.loyalty.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Traditional Brand Equity</span>
                      <span className="text-foreground font-semibold">{((metrics.traditional.awareness + metrics.traditional.trust + metrics.traditional.quality + metrics.traditional.loyalty) / 4).toFixed(1)}%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab('deepdive')}
                    className="mt-4 w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-lg transition-colors text-sm"
                  >
                    View Details
                  </button>
                </div>

                {/* Digital Summary */}
                <div className="bg-card rounded-2xl p-6 border border-[var(--border)]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-foreground">Digital Health</h3>
                    <i className="fas fa-globe text-primary text-2xl"></i>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-text-muted">Sentiment</span>
                      <span className="text-foreground font-semibold">{metrics.digital?.sentiment_score?.toFixed(1) || '0.0'}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Engagement</span>
                      <span className="text-foreground font-semibold">{metrics.digital?.engagement_rate?.toFixed(1) || '0.0'}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Mentions</span>
                      <span className="text-foreground font-semibold">{metrics.digital?.mentions_count?.toLocaleString() || '0'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-muted">Digital Brand Equity</span>
                      <span className="text-foreground font-semibold">{((metrics.digital?.sentiment_score || 0) * 0.4 + (metrics.digital?.share_of_voice || 0) * 0.3 + (metrics.digital?.engagement_rate || 0) * 6).toFixed(1)}%</span>
                    </div>
                  </div>
                  <button
                    onClick={() => setActiveTab('digital')}
                    className="mt-4 w-full py-2 bg-cyan-600 hover:bg-cyan-700 text-white font-medium rounded-lg transition-colors text-sm"
                  >
                    View Details
                  </button>
                </div>
              </div>

              {/* Comprehensive Metrics Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Brand Health Radar */}
                {combinedData && (
                  <div className="bg-card rounded-2xl p-6 border border-[var(--border)]">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-foreground">Brand Health Overview</h3>
                      <ExportMenu
                        data={metrics}
                        chartElementId="brand-health-radar"
                        exportType="chart"
                        filename={`${metrics.brand_name.toLowerCase().replace(/\s+/g, '_')}_health_overview`}
                      />
                    </div>
                    <div className="h-96" id="brand-health-radar">
                      <Radar data={combinedData} options={chartOptions} />
                    </div>
                  </div>
                )}

                {/* Product Attributes Radar */}
                {additionalMetricsData && (
                  <div className="bg-card rounded-2xl p-6 border border-[var(--border)]">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-foreground">Product Attributes</h3>
                      <ExportMenu
                        data={metrics}
                        chartElementId="product-attributes-radar"
                        exportType="chart"
                        filename={`${metrics.brand_name.toLowerCase().replace(/\s+/g, '_')}_attributes`}
                      />
                    </div>
                    <div className="h-96" id="product-attributes-radar">
                      <Radar data={additionalMetricsData} options={chartOptions} />
                    </div>
                  </div>
                )}
              </div>

              {/* Brand Equity Trend Chart */}
              {selectedBrand && isDemoMode && (
                <div className="bg-card rounded-2xl p-6 border border-[var(--border)] relative overflow-hidden">
                  {/* New Banner */}
                  <div
                    className="absolute top-0 right-0 text-white text-sm font-bold px-4 py-1.5 rounded-bl-xl shadow-lg"
                    style={{
                      animation: 'flashColors 1s ease-in-out infinite',
                      background: 'linear-gradient(90deg, #10b981, #facc15)'
                    }}
                  >
                    ✨ NEW
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">Brand Equity Trend</h3>
                      <p className="text-sm text-text-muted">Traditional vs Digital Equity over time</p>
                    </div>
                    <ExportMenu
                      data={metrics}
                      chartElementId="equity-trend-chart"
                      exportType="chart"
                      filename={`${metrics.brand_name.toLowerCase().replace(/\s+/g, '_')}_equity_trend`}
                    />
                  </div>
                  <div className="h-80" id="equity-trend-chart">
                    <Line
                      data={{
                        labels: availableMonths.map(m => m.label.split(' ')[0]), // Jun, Jul, Aug, etc.
                        datasets: [
                          {
                            label: 'Traditional Equity',
                            data: getBrandTrendData(selectedBrand).map(item =>
                              (item.data.metrics.awareness * 100 + item.data.metrics.trust * 100 +
                               item.data.metrics.quality * 100 + item.data.metrics.loyalty * 100) / 4
                            ),
                            borderColor: 'rgb(34, 197, 94)',
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                          },
                          {
                            label: 'Digital Equity',
                            data: getBrandTrendData(selectedBrand).map(item => item.data.digital.sentiment_score),
                            borderColor: 'rgb(6, 182, 212)',
                            backgroundColor: 'rgba(6, 182, 212, 0.1)',
                            fill: true,
                            tension: 0.4,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                          },
                          {
                            label: 'Total Brand Equity',
                            data: getBrandTrendData(selectedBrand).map(item =>
                              ((item.data.metrics.awareness * 100 + item.data.metrics.trust * 100 +
                                item.data.metrics.quality * 100 + item.data.metrics.loyalty * 100) / 4 * 0.6 +
                               item.data.digital.sentiment_score * 0.4)
                            ),
                            borderColor: 'rgb(168, 85, 247)',
                            backgroundColor: 'rgba(168, 85, 247, 0.1)',
                            fill: false,
                            tension: 0.4,
                            pointRadius: 4,
                            pointHoverRadius: 6,
                            borderDash: [5, 5],
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            position: 'top' as const,
                            labels: {
                              color: isDark ? '#E5E7EB' : '#374151',
                              usePointStyle: true,
                              pointStyle: 'circle',
                            }
                          },
                          tooltip: {
                            mode: 'index' as const,
                            intersect: false,
                            callbacks: {
                              label: function(context: any) {
                                return `${context.dataset.label}: ${context.parsed.y.toFixed(1)}%`;
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            grid: {
                              color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                            },
                            ticks: {
                              color: isDark ? '#9CA3AF' : '#4B5563',
                            }
                          },
                          y: {
                            min: 50,
                            max: 100,
                            grid: {
                              color: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                            },
                            ticks: {
                              color: isDark ? '#9CA3AF' : '#4B5563',
                              callback: function(value: any) {
                                return value + '%';
                              }
                            }
                          }
                        },
                        interaction: {
                          mode: 'nearest' as const,
                          axis: 'x' as const,
                          intersect: false
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Detailed Metrics Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div className="bg-card rounded-xl p-3 border border-[var(--border)]">
                  <p className="text-xs text-text-muted">Taste</p>
                  <p className="text-lg font-bold text-foreground">{((metrics.traditional as any).taste || 50).toFixed(1)}%</p>
                </div>
                <div className="bg-card rounded-xl p-3 border border-[var(--border)]">
                  <p className="text-xs text-text-muted">Texture</p>
                  <p className="text-lg font-bold text-foreground">{((metrics.traditional as any).texture || 50).toFixed(1)}%</p>
                </div>
                <div className="bg-card rounded-xl p-3 border border-[var(--border)]">
                  <p className="text-xs text-text-muted">Natural</p>
                  <p className="text-lg font-bold text-foreground">{((metrics.traditional as any).natural || 45).toFixed(1)}%</p>
                </div>
                <div className="bg-card rounded-xl p-3 border border-[var(--border)]">
                  <p className="text-xs text-text-muted">Recent Usage</p>
                  <p className="text-lg font-bold text-foreground">{metrics.traditional.recent_usage.toFixed(1)}%</p>
                </div>
                <div className="bg-card rounded-xl p-3 border border-[var(--border)]">
                  <p className="text-xs text-text-muted">Primary Brand</p>
                  <p className="text-lg font-bold text-foreground">{metrics.traditional.primary_brand.toFixed(1)}%</p>
                </div>
                <div className="bg-card rounded-xl p-3 border border-[var(--border)]">
                  <p className="text-xs text-text-muted">Responses</p>
                  <p className="text-lg font-bold text-foreground">{metrics.traditional.total_responses.toLocaleString()}</p>
                </div>
              </div>

              {/* Dynamic AI Insights */}
              <div className="bg-card rounded-2xl p-6 border border-[var(--border)]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-semibold text-foreground">AI-Generated Insights</h3>
                  {insightsLoading && (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
                      <span className="text-sm text-text-muted">Analyzing...</span>
                    </div>
                  )}
                </div>
                <div className="space-y-3 min-h-[150px]">
                  {insightsLoading ? (
                    <div className="flex flex-col items-center justify-center h-32">
                      <div className="animate-pulse space-y-3 w-full">
                        <div className="h-4 bg-background-tertiary rounded w-full"></div>
                        <div className="h-4 bg-background-tertiary rounded w-5/6"></div>
                        <div className="h-4 bg-background-tertiary rounded w-4/5"></div>
                      </div>
                      <p className="text-text-muted text-sm mt-4">Generating AI insights for {metrics?.brand_name}...</p>
                    </div>
                  ) : insights.length > 0 ? (
                    insights.map((insight, index) => (
                      <div key={index} className="flex items-start space-x-3 animate-fadeIn">
                        <i className={`fas mt-1 ${
                          index === 0 ? 'fa-chart-line text-primary' :
                          index === 1 ? 'fa-award text-green-400' :
                          index === 2 ? 'fa-bullseye text-yellow-400' :
                          index === 3 ? 'fa-globe text-purple-400' :
                          index === 4 ? 'fa-trophy text-secondary' :
                          index === 5 ? 'fa-dollar-sign text-emerald-400' :
                          'fa-lightbulb text-orange-400'
                        }`}></i>
                        <div className="text-text-secondary text-sm leading-relaxed prose prose-sm prose-invert max-w-none">
                          <ReactMarkdown
                            components={{
                              p: ({children}) => <p className="mb-2">{children}</p>,
                              ul: ({children}) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                              ol: ({children}) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                              li: ({children}) => <li className="mb-1">{children}</li>,
                              strong: ({children}) => <strong className="font-semibold text-foreground">{children}</strong>,
                              em: ({children}) => <em className="italic">{children}</em>,
                              code: ({children}) => <code className="bg-background-tertiary px-1 py-0.5 rounded text-primary">{children}</code>,
                            }}
                          >
                            {insight}
                          </ReactMarkdown>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="flex items-center justify-center h-32">
                      <p className="text-text-muted">No insights available. Select a brand to analyze.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'deepdive' && (
            <DeepDiveDashboard 
              brandId={selectedBrand}
              brandName={metrics.brand_name}
              metrics={metrics.traditional}
              demographicFilters={demographicFilters}
            />
          )}

          {activeTab === 'digital' && (
            <DigitalMetricsDashboard 
              metrics={digitalMetrics || metrics.digital} 
              brandName={metrics.brand_name} 
            />
          )}
        </>
      )}

      {activeTab === 'comparison' && (
        <BrandComparison />
      )}

      {activeTab === 'posts' && metrics && (
        <PostsDashboard
          brandId={selectedBrand}
          brandName={metrics.brand_name}
        />
      )}
      </div>

    </div>
  );
};

export default DashboardPage;
